import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, signalStoreFeature, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { PagedResponse, QueryFiltersParams, QueryPagingParams } from 'mbs-ui-shared@models/Paging';
import { DomainInfo, DomainStatisticsFilterConverted } from 'mbs-ui-shared@models/domain.model';
import { DomainsService } from 'mbs-ui-shared@services/domains.service';
import { withPagingAndData } from 'mbs-ui-shared@store/withPagingAndDataStoreFeature/withPagingAndData';
import { Observable, pipe } from 'rxjs';
import { filter, mergeMap } from 'rxjs/operators';
import { CustomDomainsStoreProperties, DomainStatisticParams } from './domainsStore.types';

// ---------------------------------------------------------------------------
// Custom feature: restricted names + domain statistics
// ---------------------------------------------------------------------------

function withDomainsFeature() {
  return signalStoreFeature(
    withState<CustomDomainsStoreProperties>({
      restrictedNames: [],
      loadedRestrictedNames: false,
      domainStatistic: null,
      loadedDomainStatistic: false,
      loadingDomainStatistic: false
    }),
    withComputed((store) => ({
      selectRestrictedNames: computed(() => store.restrictedNames()),
      selectLoadedRestrictedNames: computed(() => store.loadedRestrictedNames()),
      selectDomainStatistic: computed(() => store.domainStatistic()),
      selectLoadedDomainStatistic: computed(() => store.loadedDomainStatistic()),
      selectLoadingDomainStatistic: computed(() => store.loadingDomainStatistic())
    })),
    withMethods((store, domainsService = inject(DomainsService)) => ({
      loadRestrictedNames: rxMethod<DomainStatisticParams | void>(
        pipe(
          filter((params) => !!(params as DomainStatisticParams)?.force || !store.loadedRestrictedNames()),
          mergeMap(() =>
            domainsService.getRestrictedNames().pipe(
              tapResponse({
                next: (names: string[]) => patchState(store, { restrictedNames: names, loadedRestrictedNames: true }),
                error: () => void 0
              })
            )
          )
        )
      ),

      loadDomainStatistic: rxMethod<DomainStatisticParams | void>(
        pipe(
          filter((params) => !!(params as DomainStatisticParams)?.force || !store.loadedDomainStatistic()),
          mergeMap((params) => {
            patchState(store, { loadingDomainStatistic: true });

            return domainsService.getDomainStatistic((params as DomainStatisticParams)?.companyId).pipe(
              tapResponse({
                next: (stat) =>
                  patchState(store, {
                    domainStatistic: {
                      total: stat?.total,
                      ...stat?.data?.reduce((acc, curr) => ({ ...acc, [curr.status]: { count: curr.count } }), {})
                    },
                    loadedDomainStatistic: stat != null,
                    loadingDomainStatistic: false
                  }),
                error: () => patchState(store, { domainStatistic: null, loadingDomainStatistic: false })
              })
            );
          })
        )
      ),

      setDomainStatistic: (stat: DomainStatisticsFilterConverted | null): void => {
        patchState(store, { domainStatistic: stat, loadedDomainStatistic: stat !== null });
      }
    }))
  );
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const DomainsStore = signalStore(
  { providedIn: 'root' },
  withDevtools('DomainsStore'),
  withPagingAndData<DomainInfo>({
    selectId: (entity) => entity.id,
    getEntityById: () => {
      const service = inject(DomainsService);
      return (id: string): Observable<DomainInfo> => service.getById(id);
    },
    getEntitiesByIds: () => null,
    getEntities: () => {
      const service = inject(DomainsService);
      return (params: QueryFiltersParams & QueryPagingParams): Observable<PagedResponse<DomainInfo>> => service.get(params);
    }
  }),
  withDomainsFeature()
);

export type DomainsStore = InstanceType<typeof DomainsStore>;
